/**
 * Public origin for `/uploads/*` when the SPA is not served by the same process
 * (e.g. nginx only proxies `/api` — set this to `https://your-api-host` with no trailing slash).
 */
const ASSET_ORIGIN = (
  process.env.REACT_APP_ASSET_ORIGIN ||
  process.env.REACT_APP_API_PUBLIC_ORIGIN ||
  ""
).replace(/\/$/, "");

/**
 * Resolve product/image URLs for correct host in dev vs production.
 * @param {string} url - Image or resource URL
 * @param {string} [apiBaseUrl] - Base URL of the API (e.g. from axios.defaults.baseURL)
 */
export function resolveUrl(url, apiBaseUrl) {
  if (!url) return "";
  let host = apiBaseUrl ? String(apiBaseUrl).replace(/\/api\/?$/, "") : "";
  // If axios baseURL is relative (e.g. "/api"), derive the real origin from the browser.
  // This prevents mixed-content issues when DB contains absolute http:// IP URLs.
  if (
    (!host || host.startsWith("/")) &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.origin
  ) {
    host = window.location.origin;
  }
  if (url.startsWith("/uploads/") && ASSET_ORIGIN) return `${ASSET_ORIGIN}${url}`;
  if (url.startsWith("/uploads/") && host) return `${host}${url}`;
  // Only rewrite absolute URLs when they point to our uploads directory.
  // External image hosts (e.g. placeholder seeds) must remain unchanged.
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      // If our CMS stored an absolute http:// URL for our own site, upgrade it to https to prevent mixed content.
      // Keep true external hosts unchanged.
      const hostname = String(parsed.hostname || "").toLowerCase();
      const currentHost =
        typeof window !== "undefined" && window.location
          ? String(window.location.hostname || "").toLowerCase()
          : "";
      const isLocal =
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "::1" ||
        hostname.endsWith(".local");
      const isSameSite =
        (currentHost && (hostname === currentHost || hostname === `www.${currentHost}` || `www.${hostname}` === currentHost)) ||
        hostname === "alramlaa.com" ||
        hostname === "www.alramlaa.com";
      // Normalize same-site URLs to the page's own origin (https, no www).
      // This fixes: mixed content (http→https) and www≠non-www cross-origin blocking.
      if (!isLocal && isSameSite) {
        const pageOrigin =
          typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : `https://${hostname.replace(/^www\./, "")}`;
        return `${pageOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
      if (parsed.pathname && (parsed.pathname.startsWith("/uploads/") || parsed.pathname.startsWith("/api/uploads/"))) {
        return host ? url.replace(parsed.origin, host) : url;
      }
      return url;
    } catch {
      return url;
    }
  }
  return url;
}

/**
 * Normalized slug for `store.categories.<slug>` (matches API slugs like `fire-rated-doors` → `firerateddoors`).
 * @param {{ label?: string, slug?: string } | string} cat
 */
export function storeCategorySlug(cat) {
  if (cat == null) return "";
  if (typeof cat === "string") {
    return cat
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/[\s-]+/g, "");
  }
  if (typeof cat !== "object") return "";
  const raw =
    typeof cat.slug === "string" && cat.slug.trim()
      ? cat.slug.trim()
      : String(cat.label || "");
  if (!raw) return "";
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "");
}

/**
 * @param {function} t - from useTranslation("global")
 * @param {{ label?: string, slug?: string } | string} cat
 */
export function translateStoreCategory(t, cat) {
  const slug = storeCategorySlug(cat);
  const fallback =
    typeof cat === "string"
      ? cat
      : cat?.label || slug || "";
  if (!slug) return fallback;
  return t(`store.categories.${slug}`, { defaultValue: fallback });
}

/** @param {string} [lang] - i18n.language */
export function isStoreArabicLang(lang) {
  return String(lang || "en").toLowerCase().startsWith("ar");
}

/**
 * Display name for a store product (uses `arName` when UI is Arabic).
 * @param {{ name?: string, arName?: string } | null | undefined} product
 * @param {string} [lang] - i18n.language
 */
export function localizedStoreProductName(product, lang) {
  if (!product) return "";
  const en = String(product.name || "").trim();
  const ar = String(product.arName || "").trim();
  if (isStoreArabicLang(lang)) {
    return ar || en;
  }
  return en || ar;
}

/**
 * Long product description (uses `arDescription` when UI is Arabic).
 * @param {{ description?: string, arDescription?: string } | null | undefined} product
 * @param {string} [lang] - i18n.language
 */
export function localizedStoreProductDescription(product, lang) {
  if (!product) return "";
  const en = String(product.description || "").trim();
  const ar = String(product.arDescription || "").trim();
  if (isStoreArabicLang(lang)) {
    return ar || en;
  }
  return en || ar;
}

function storePriceLocale(lang) {
  return isStoreArabicLang(lang) ? "ar-SA" : "en-US";
}

/**
 * `Intl` for `ar-SA` uses ٬ (U+066C) thousands and ٫ (U+066B) decimals — often reads like a tick (`'`).
 * Normalize to ASCII `,` / `.` so amounts match the familiar "2,499.00" pattern with Arabic-Indic digits.
 */
function normalizeArabicPriceSeparators(formatted) {
  if (!formatted) return "";
  return String(formatted)
    .replace(/\u066c/g, ",")
    .replace(/\u066b/g, ".");
}

/**
 * Locale-formatted number (e.g. Arabic-Indic digits in ar-SA).
 */
export function formatStorePriceAmount(amount, lang, fractionDigits = 2) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const raw = new Intl.NumberFormat(storePriceLocale(lang || "en"), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
  return isStoreArabicLang(lang) ? normalizeArabicPriceSeparators(raw) : raw;
}

/**
 * Plain-text price for mailto, logs, etc. Uses "SAR" (no icon).
 */
export function formatStorePricePlain(amount, lang) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `${formatStorePriceAmount(n, lang, 2)} SAR`;
}

/**
 * Full price string fallback (prefer {@link StorePriceDisplay} in UI for Arabic + SAR).
 * @param {function} t - useTranslation("global").t
 * @param {number|string} amount
 * @param {string} [lang] - i18n.language
 */
export function formatStorePrice(t, amount, lang) {
  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return t("store.priceMissing", { defaultValue: "—" });
  }
  const formatted = formatStorePriceAmount(n, lang, 2);
  if (String(lang || "en").toLowerCase().startsWith("ar")) {
    return `${formatted} SAR`;
  }
  return t("store.priceWithCurrency", { amount: formatted, defaultValue: "{{amount}} SAR" });
}

export function formatStorePriceEach(t, amount, lang) {
  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return t("store.priceMissing", { defaultValue: "—" });
  }
  const formatted = formatStorePriceAmount(n, lang, 2);
  if (String(lang || "en").toLowerCase().startsWith("ar")) {
    const suffix = t("store.priceEachSuffix", { defaultValue: "each" });
    return `${formatted} SAR ${suffix}`.trim();
  }
  return t("store.priceEach", { amount: formatted, defaultValue: "{{amount}} SAR each" });
}

/**
 * For order totals when API returns a currency code (e.g. SAR, USD).
 */
export function formatStoreMoney(t, amount, lang, currencyCode = "SAR") {
  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return t("store.priceMissing", { defaultValue: "—" });
  }
  const formatted = formatStorePriceAmount(n, lang, 2);
  const code = (currencyCode || "SAR").toUpperCase();
  if (code === "SAR" && String(lang || "en").toLowerCase().startsWith("ar")) {
    return `${formatted} SAR`;
  }
  const currencyLabel = t(`store.currency.${code}`, { defaultValue: code });
  return t("store.priceAmountAndCode", {
    amount: formatted,
    currency: currencyLabel,
    defaultValue: "{{amount}} {{currency}}",
  });
}

/** Uses `stock_quantity` when present; falls back to legacy `inStock`. */
export function isStoreProductOutOfStock(product) {
  if (!product) return true;
  if (product.inStock === false) return true;
  const q = product.stock_quantity;
  if (typeof q === "number" && Number.isFinite(q)) return q <= 0;
  return false;
}

/** `null` = no cap (legacy product without inventory fields). */
export function getStoreProductStockCap(product) {
  const q = product?.stock_quantity;
  if (typeof q === "number" && Number.isFinite(q) && q >= 0) return Math.floor(q);
  return null;
}

/** Use API `original_price`, `final_price`, `discount_percentage` when present. */
export function getStoreProductPriceDisplay(product) {
  if (!product) {
    return {
      originalPrice: 0,
      finalPrice: 0,
      discountPercent: 0,
      hasDiscount: false,
    };
  }
  const orig = Number(product.original_price);
  const fin = Number(product.final_price);
  const list = Number(product.price) || 0;
  const pct = Number(product.discount_percentage);
  if (
    Number.isFinite(orig) &&
    Number.isFinite(fin) &&
    Number.isFinite(pct) &&
    pct > 0 &&
    fin < orig - 0.001
  ) {
    return {
      originalPrice: orig,
      finalPrice: fin,
      discountPercent: Math.floor(pct),
      hasDiscount: true,
    };
  }
  return {
    originalPrice: list,
    finalPrice: Number.isFinite(fin) ? fin : list,
    discountPercent: 0,
    hasDiscount: false,
  };
}

export function getStoreCartLineUnitPrice(item) {
  if (!item) return 0;
  // When options-based pricing is applied, cart lines may carry a computed override.
  const override = Number(item.price_override);
  if (Number.isFinite(override) && override >= 0) return override;
  const f = Number(item.final_price);
  if (Number.isFinite(f) && f >= 0) return f;
  return Math.max(0, Number(item.price) || 0);
}

function roundMoney(n) {
  return Math.max(0, Math.round(Number(n) * 100) / 100);
}

export function computeCurtainUnitPrice(product, options = {}) {
  const cfg = product?.curtainPricing;
  if (!cfg || cfg.enabled === false) return null;
  const widthKey = options.selectedWidth != null ? String(options.selectedWidth) : "";
  const heightKey = options.selectedHeight != null ? String(options.selectedHeight) : "";
  if (!widthKey || !heightKey) return null;

  // New mode: base minimum + add-ons.
  if (cfg.basePrice !== undefined && cfg.basePrice !== null && cfg.basePrice !== "") {
    const basePrice = Number(cfg.basePrice);
    if (!Number.isFinite(basePrice)) return null;
    const widthAddons = cfg.widthAddons && typeof cfg.widthAddons === "object" ? cfg.widthAddons : {};
    const heightAddons = cfg.heightAddons && typeof cfg.heightAddons === "object" ? cfg.heightAddons : {};
    const adjustmentAddons =
      cfg.adjustmentAddons && typeof cfg.adjustmentAddons === "object"
        ? cfg.adjustmentAddons
        : cfg.adjustments && typeof cfg.adjustments === "object"
        ? cfg.adjustments
        : {};
    const chainAddons =
      cfg.chainTypeAddons && typeof cfg.chainTypeAddons === "object"
        ? cfg.chainTypeAddons
        : cfg.chainType && typeof cfg.chainType === "object"
        ? cfg.chainType
        : {};

    const wAdd = Number.isFinite(Number(widthAddons[widthKey])) ? Number(widthAddons[widthKey]) : 0;
    const hAdd = Number.isFinite(Number(heightAddons[heightKey])) ? Number(heightAddons[heightKey]) : 0;
    const wAdjKey = String(options.widthAdjustment || "none");
    const hAdjKey = String(options.heightAdjustment || "none");
    const wAdj = Number.isFinite(Number(adjustmentAddons[wAdjKey])) ? Number(adjustmentAddons[wAdjKey]) : 0;
    const hAdj = Number.isFinite(Number(adjustmentAddons[hAdjKey])) ? Number(adjustmentAddons[hAdjKey]) : 0;
    const chainKey = String(options.chainType || "");
    const cAdj = chainKey && Number.isFinite(Number(chainAddons[chainKey])) ? Number(chainAddons[chainKey]) : 0;

    const original = roundMoney(basePrice + wAdd + hAdd + wAdj + hAdj + cAdj);
    const d = Math.max(0, Math.min(100, Math.floor(Number(product?.discount_percentage) || 0)));
    const final = d > 0 ? roundMoney(original - (original * d) / 100) : original;
    return { originalPrice: original, finalPrice: final, discountPercent: d };
  }

  // Legacy mode (absolute width base + height add-ons).
  const widthPrices = cfg.widthPrices && typeof cfg.widthPrices === "object" ? cfg.widthPrices : {};
  const heightAddons = cfg.heightAddons && typeof cfg.heightAddons === "object" ? cfg.heightAddons : {};
  const adjustments = cfg.adjustments && typeof cfg.adjustments === "object" ? cfg.adjustments : {};
  const chainType = cfg.chainType && typeof cfg.chainType === "object" ? cfg.chainType : {};
  const base = Number(widthPrices[widthKey]);
  if (!Number.isFinite(base)) return null;
  const hAdd = Number.isFinite(Number(heightAddons[heightKey])) ? Number(heightAddons[heightKey]) : 0;
  const wAdjKey = String(options.widthAdjustment || "none");
  const hAdjKey = String(options.heightAdjustment || "none");
  const wAdj = Number.isFinite(Number(adjustments[wAdjKey])) ? Number(adjustments[wAdjKey]) : 0;
  const hAdj = Number.isFinite(Number(adjustments[hAdjKey])) ? Number(adjustments[hAdjKey]) : 0;
  const chainKey = String(options.chainType || "");
  const cAdj = chainKey && Number.isFinite(Number(chainType[chainKey])) ? Number(chainType[chainKey]) : 0;
  const original = roundMoney(base + hAdd + wAdj + hAdj + cAdj);
  const d = Math.max(0, Math.min(100, Math.floor(Number(product?.discount_percentage) || 0)));
  const final = d > 0 ? roundMoney(original - (original * d) / 100) : original;
  return { originalPrice: original, finalPrice: final, discountPercent: d };
}

export function formatStoreDeliveryEstimate(product, t) {
  const min = product?.min_delivery_days;
  const max = product?.max_delivery_days;
  if (typeof min !== "number" || typeof max !== "number") return "";
  const a = Math.max(0, Math.floor(min));
  const b = Math.max(a, Math.floor(max));
  return t("store.expectedDeliveryRange", {
    min: a,
    max: b,
    defaultValue: "Expected delivery: {{min}} - {{max}} days",
  });
}
